<template>
  <a-card :bordered="false" class="transaction-records">
    <div class="table-page-search-wrapper">
      <a-form layout="inline">
        <a-row :gutter="16" class="search-row">
          <a-col :md="8" :sm="24">
            <a-form-item :label="$t('date')">
              <a-range-picker
                format="DD-MM-YYYY"
                valueFormat="YYYY-MM-DD"
                v-model="queryParam.dateRange"
                style="width: 100%"
                :placeholder="[$t('platformCollection.start_time'), $t('platformCollection.end_time')]"
                @change="onDateChange"
              />
            </a-form-item>
          </a-col>
          <a-col :md="8" :sm="24">
            <a-input-group compact>
              <a-select v-model="queryParam.searchType" style="width: 35%">
                <a-select-option value="id">{{ $t('investorList.zcbid') }}</a-select-option>
                <a-select-option value="investTitle">{{ $t('investorList.zcbmc') }}</a-select-option>
              </a-select>
              <a-input v-model="queryParam.keyword" style="width: 65%" :placeholder="$t('enter')" allow-clear />
            </a-input-group>
          </a-col>
          <a-col :md="4" :sm="24">
            <a-button type="primary" @click="handleSearch">{{ $t('search') }}</a-button>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <a-table
      bordered
      rowKey="id"
      :columns="columns"
      :dataSource="dataSource"
      :pagination="pagination"
      :loading="loading"
      @change="handleTableChange"
    >
      <span slot="matchDate" slot-scope="text, record">
        {{ formatMatchDate(record) }}
      </span>
      <span slot="investAmount" slot-scope="text">
        {{ text | FenToDong }}
      </span>
      <span slot="action" slot-scope="text, record">
        <a @click="viewBorrowerDetail(record)">{{ $t('investorList.jkrmx') }}</a>
        <a-divider type="vertical" />
        <a @click="viewInvestorDetail(record)">{{ $t('investorList.tzrmx') }}</a>
      </span>
    </a-table>
    <borrower-detail-modal ref="borrowerDetailModal" />
    <investor-details-modal ref="investorDetailModal" />
  </a-card>
</template>

<script>
import { getTransactionRecords } from '@/webpublicapi/vietnamBackstage'
import moment from 'moment'
import BorrowerDetailModal from './modules/BorrowerDetailModal.vue'
import InvestorDetailsModal from './modules/InvestorDetailsModal.vue'

export default {
  name: 'TransactionRecords',
  components: {
    BorrowerDetailModal,
    InvestorDetailsModal,
  },
  data() {
    return {
      queryParam: {
        dateRange: [],
        begin: '',
        end: '',
        searchType: 'id',
        keyword: '',
      },
      dataSource: [],
      loading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total) => `${this.$t('components.total')} ${total} ${this.$t('components.items')}`,
      },
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('investorList.pprq'),
          dataIndex: 'investReachedDate',
          align: 'center',
          scopedSlots: { customRender: 'matchDate' },
        },
        { title: this.$t('investorList.zcbid'), dataIndex: 'investId', align: 'center' },
        { title: this.$t('investorList.zcbmc'), dataIndex: 'investTitle', align: 'center' },
        {
          title: this.$t('investorList.zje'),
          dataIndex: 'investAllAmount',
          align: 'center',
          scopedSlots: { customRender: 'investAmount' },
        },
        { title: this.$t('investorList.jkrs'), dataIndex: 'investLoanCount', align: 'center' },
        { title: this.$t('investorList.tzrs'), dataIndex: 'investorCount', align: 'center' },
        {
          title: this.$t('investorList.cz'),
          dataIndex: 'action',
          align: 'center',
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
  },
  created() {
    const todayStart = moment().startOf('day')
    const todayEnd = moment().endOf('day')
    this.queryParam.dateRange = [todayStart, todayEnd]
    this.queryParam.begin = todayStart.format('YYYY-MM-DD')
    this.queryParam.end = todayEnd.format('YYYY-MM-DD')
    this.loadData()
  },
  methods: {
    onDateChange(dates, dateStrings) {
      if (!dates || dates.length === 0) {
        this.queryParam.begin = ''
        this.queryParam.end = ''
        return
      }
      this.queryParam.begin = this.queryParam.dateRange[0]
      this.queryParam.end = this.queryParam.dateRange[1]
    },
    formatMatchDate(record) {
      let date = record.investReachedDate || record.matchDate || record.createdTime || record.updateTime
      return date ? this.transformTime(date, 2) : '---'
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    buildParams() {
      const keyword = (this.queryParam.keyword || '').trim()
      const rawParams = {
        pageNo: this.pagination.current,
        pageSize: this.pagination.pageSize,
        begin: this.queryParam.begin ? `${this.queryParam.begin} 00:00:00` : '',
        end: this.queryParam.end ? `${this.queryParam.end} 23:59:59` : '',
      }

      if (keyword) {
        if (this.queryParam.searchType === 'id') {
          rawParams.id = keyword
        } else if (this.queryParam.searchType === 'investTitle') {
          rawParams.investTitle = keyword
        }
      }

      return Object.keys(rawParams).reduce((acc, key) => {
        const value = rawParams[key]
        if (value !== '' && value !== undefined && value !== null) {
          acc[key] = value
        }
        return acc
      }, {})
    },
    loadData() {
      this.loading = true
      getTransactionRecords(this.buildParams())
        .then((res) => {
          if (res.success || res.respCode === 1000 || res.code === 200) {
            const data = res.respData || res.result || {}
            this.dataSource = data.records || []
            this.pagination.total = data.total || 0
          } else {
            this.$message.warning(res.respMsg || res.message || this.$t('platformCollection.query_failed'))
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    viewBorrowerDetail(record) {
      // open borrower detail modal
      if (this.$refs.borrowerDetailModal) {
        this.$refs.borrowerDetailModal.show(record.investId)
      } else {
        // fallback message
        this.$message.info(`${this.$t('platformCollection.borrower_detail')}: ${record.idMask || record.id || ''}`)
        console.log(record.investId)
      }
    },
    viewInvestorDetail(record) {
      if (this.$refs.investorDetailModal) {
        // Pass investId, annual interest rate, and total amount to detail modal
        this.$refs.investorDetailModal.show({
          investId: record.investId,
          investYieldRate: record.investYieldRate,
          investAllAmount: record.investAllAmount,
        })
      } else {
        this.$message.info(`${this.$t('platformCollection.investor_detail')}: ${record.idMask || record.id || ''}`)
      }
    },
  },
}
</script>

<style lang="less" scoped>
@import '~@/assets/less/common.less';

.transaction-records {
  .table-page-search-wrapper {
    margin-bottom: 16px;
  }
}
</style>
